<template>
  <Flex direction="column">
    <div :class="$style.header">
      <BaseHeading>{{ $t('service.nav.resources.title') }}</BaseHeading>
      <BaseText
        v-if="!selected"
        iconBefore="edit"
      >
        {{ $t('global.actions.edit') }}
      </BaseText>
    </div>
    <div v-if="requirementsPerVariation">
      {{ $t('service.resources.different_per_variation') }}
    </div>
    <Flex
      v-else
      direction="column"
    >
      <div
        v-for="requirement in visibleRequirements"
        :key="requirement.id"
        :class="$style.item"
      >
        <BaseIcon
          size="s"
          :name="getIconPerRequirementType(requirement.type)"
          :color="
            isEdit && !requirement.resourceIds.length ? 'warning' : 'default'
          "
        />
        <BaseText :italic="isEdit && !requirement.resourceIds.length">
          {{ getResourceLabel(requirement) }}
        </BaseText>
      </div>
    </Flex>
  </Flex>
</template>

<script
  setup
  lang="ts"
>
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useResourcesStore } from '@/stores/resources';
import type { ServiceVariationsFormData } from '../types';

const route = useRoute();
const isEdit = computed(() => !!route.params.serviceId);

defineProps<{
  selected: boolean;
}>();

const formData = inject<ServiceVariationsFormData>('serviceFormData')!;
const requirementsPerVariation = inject<ServiceVariationsFormData>(
  'requirementsPerVariation',
)!;

const { resourcesByType } = useResourcesStore();

const { t } = useI18n();

const getIconPerRequirementType = (type: string) => {
  switch (type.toLocaleLowerCase()) {
    case 'employee':
      return 'person';
    case 'room':
      return 'room';
    case 'equipment':
      return 'monitor';
    default:
      return 'person';
  }
};

const visibleRequirements = computed(() =>
  formData.requirementsAttributes.filter((r: any) => !r.destroy),
);

const getResourceLabel = (requirement) => {
  const resourcesList = resourcesByType(requirement.type.toLowerCase());

  if (requirement.resourceIds.length === resourcesList.length) {
    return t(`service.resources.${requirement.type.toLowerCase()}.all`);
  }

  if (requirement.resourceIds.length === 0 && requirement.primary) {
    return t('service.resources.no_primary');
  }

  if (requirement.resourceIds.length === 0) {
    return t('service.resources.none');
  }

  if (requirement.resourceIds.length > 3) {
    const names = resourcesList
      .filter((resource) => requirement.resourceIds.includes(resource.id))
      .filter((_i, i: number) => i < 3)
      .map((resource) => resource.name)
      .join(', ');
    return t('service.resources.and_more', {
      selected_resources: names,
      amount: requirement.resourceIds.length - 3,
    });
  }

  return resourcesList
    .filter((resource) => requirement.resourceIds.includes(resource.id))
    .map((resource) => resource.name)
    .join(', ');
};
</script>

<style
  module
  lang="scss"
>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item {
  display: flex;
  gap: $spacing * 0.25;
  align-items: center;
}
</style>
